/**
 * What each admin resource lists: its columns (sortable where the API
 * whitelists them) and its filters. Detail views show every field the API
 * returns, so they need no configuration beyond where nested ids link.
 */
export interface Column {
	key: string;
	title?: string;
	sort?: boolean;
	/** Resource this column's value links to, when the field name doesn't say. */
	link?: string;
}

export interface Filter {
	key: string;
	title: string;
	options: string[];
}

export interface ResourceConfig {
	name: string;
	label: string;
	group: 'data' | 'pipeline' | 'ops';
	columns: Column[];
	filters?: Filter[];
	search?: boolean;
	/** Nested tables in the detail view whose `id` links to a resource. */
	nested?: Record<string, string>;
	defaultSort?: { field: string; order: 'asc' | 'desc' };
}

const col = ( key: string, sort = false, link?: string ): Column => ( { key, sort, ...( link ? { link } : {} ) } );

export const RESOURCES: ResourceConfig[] = [
	{
		name: 'competitions',
		label: 'Competitions',
		group: 'data',
		columns: [ col( 'id', true, 'competitions' ), col( 'name', true ), col( 'sport', true ), col( 'profile', true ), col( 'country', true ), col( 'current_season', true ), col( 'seasons' ), col( 'is_active', true ) ],
		search: true,
		nested: { bindings: 'bindings' },
	},
	{
		name: 'seasons',
		label: 'Seasons',
		group: 'data',
		columns: [ col( 'id', true, 'seasons' ), col( 'competition', true ), col( 'label' ), col( 'status', true ), col( 'is_current', true ), col( 'start_date' ), col( 'end_date' ), col( 'events' ), col( 'finished' ) ],
		filters: [ { key: 'status', title: 'Status', options: [ 'running', 'upcoming', 'finished' ] } ],
		search: true,
	},
	{
		name: 'events',
		label: 'Events',
		group: 'data',
		columns: [ col( 'id', false, 'events' ), col( 'scheduled_start', true ), col( 'competition', true ), col( 'round', true ), col( 'name' ), col( 'score' ), col( 'status', true ), col( 'updated_at', true ) ],
		filters: [
			{ key: 'status', title: 'Status', options: [ 'scheduled', 'live', 'half_time', 'finished', 'postponed', 'cancelled' ] },
			{ key: 'kind', title: 'Kind', options: [ 'match', 'meeting', 'session' ] },
		],
		search: true,
		nested: { signals: 'signals' },
	},
	{
		name: 'teams',
		label: 'Teams',
		group: 'data',
		columns: [ col( 'id', false, 'teams' ), col( 'name', true ), col( 'short_name' ), col( 'sport', true ), col( 'kind', true ), col( 'publisher_key', true ), col( 'updated_at', true ) ],
		search: true,
	},
	{
		name: 'persons',
		label: 'People',
		group: 'data',
		columns: [ col( 'id', false, 'persons' ), col( 'name', true ), col( 'roles' ), col( 'country', true ), col( 'publisher_key', true ), col( 'updated_at', true ) ],
		search: true,
	},
	{
		name: 'bindings',
		label: 'Bindings',
		group: 'pipeline',
		columns: [ col( 'id', true, 'bindings' ), col( 'competition', true ), col( 'provider', true ), col( 'capabilities' ), col( 'is_paused', true ), col( 'last_outcome', true ), col( 'last_finished_at' ), col( 'next_due_at', true ), col( 'open_issues', true ) ],
		search: true,
	},
	{
		name: 'runs',
		label: 'Runs',
		group: 'pipeline',
		columns: [ col( 'id', true, 'runs' ), col( 'binding', true ), col( 'mode', true ), col( 'scope', true ), col( 'started_at', true ), col( 'duration_ms', true ), col( 'outcome', true ), col( 'requests', true ), col( 'inserted', true ), col( 'updated', true ), col( 'signals', true ), col( 'error' ) ],
		filters: [
			{ key: 'outcome', title: 'Outcome', options: [ 'running', 'ok', 'partial', 'failed' ] },
			{ key: 'mode', title: 'Mode', options: [ 'backfill', 'incremental', 'live', 'push', 'maintenance' ] },
		],
		search: true,
		nested: { fetches: 'fetches', emitted_signals: 'signals' },
	},
	{
		name: 'fetches',
		label: 'Fetches',
		group: 'pipeline',
		columns: [ col( 'id', true, 'fetches' ), col( 'run', true ), col( 'provider', true ), col( 'started_at', true ), col( 'url' ), col( 'attempt' ), col( 'status', true ), col( 'duration_ms', true ), col( 'bytes', true ), col( 'error' ) ],
		search: true,
	},
	{
		name: 'issues',
		label: 'Issues',
		group: 'pipeline',
		columns: [ col( 'id', true, 'issues' ), col( 'severity', true ), col( 'kind', true ), col( 'binding', true ), col( 'message' ), col( 'occurrences', true ), col( 'opened_at', true ), col( 'last_seen_at', true ), col( 'open', true ) ],
		filters: [
			{ key: 'open', title: 'Open', options: [ 'true', 'false' ] },
			{ key: 'severity', title: 'Severity', options: [ 'info', 'warning', 'error', 'critical' ] },
		],
		search: true,
	},
	{
		name: 'providers',
		label: 'Providers',
		group: 'pipeline',
		columns: [ col( 'id', true, 'providers' ), col( 'label', true ), col( 'licence' ), col( 'commercial_use', true ), col( 'bindings', true ), col( 'min_interval_ms' ), col( 'per_hour' ), col( 'hour_count' ), col( 'backoff_until' ) ],
	},
	{
		name: 'signals',
		label: 'Signals',
		group: 'ops',
		columns: [ col( 'id', true, 'signals' ), col( 'occurred_at', true ), col( 'type', true ), col( 'entity_table', true ), col( 'entity_uid', true ), col( 'run', true ), col( 'published_at' ) ],
		search: true,
	},
	{
		name: 'subscribers',
		label: 'Subscribers',
		group: 'ops',
		columns: [ col( 'id', true, 'subscribers' ), col( 'name' ), col( 'url' ), col( 'is_paused', true ), col( 'backlog', true ), col( 'consecutive_failures', true ), col( 'last_delivered_at', true ), col( 'last_status' ), col( 'last_error' ) ],
	},
	{
		name: 'dual-run-checks',
		label: 'Dual-run checks',
		group: 'ops',
		columns: [ col( 'id', true, 'dual-run-checks' ), col( 'checked_at', true ), col( 'routes' ), col( 'equal' ), col( 'accepted' ), col( 'different', true ), col( 'errors', true ) ],
	},
];

export const GROUPS: Record<ResourceConfig[ 'group' ], string> = { data: 'Data', pipeline: 'Pipeline', ops: 'Ops' };
